// src/Pages/DocumentsPage.tsx
import Layout from "../components/Layout";
import { Plus, FileText, Trash2, MessageSquare } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEntity } from "@/hooks/useEntity";
import { documentEntityConfig } from "@/entities/Document";
import { transcriptEntityConfig } from "@/entities/Transcript";
import WhiptTranscriptsPanel from "@/components/WhiptTranscriptsPanel";

type DocItem = {
  id: string | number;
  title: string;
  paperFormat?: string;
  content?: string;
  wordCount?: number;
  updated_at?: string;
};

type TranscriptItem = {
  id: string | number;
  title: string;
  transcript: string;
  questions: string[];
  answers: any[];
  audioFileName: string;
  questionsFileName: string;
  mode?: "audio" | "text";
  created_at?: string;
  updated_at?: string;
};

const DocumentsPage: React.FC = () => {
  const navigate = useNavigate();
  const docs = useEntity<DocItem>(documentEntityConfig);
  const transcripts = useEntity<TranscriptItem>(transcriptEntityConfig as any);

  const [deleteConfirm, setDeleteConfirm] = React.useState<string | number | null>(null);
  const [selectedTranscript, setSelectedTranscript] = React.useState<TranscriptItem | null>(null);
  
  // DEBUG: Alert when page loads
  React.useEffect(() => {
    alert(`Documents Page Loaded!\nDocs: ${docs.items.length}\nLoading: ${docs.loading}\nError: ${docs.error || 'none'}`);
  }, [docs.items.length, docs.loading, docs.error]);

  const handleOpenDoc = (id: string | number) => {
    // Find the document
    const doc = docs.items.find(d => d.id === id);
    if (!doc) return;
    
    // Check if this is an uploaded file (has base64 content)
    if (doc.content && doc.content.startsWith('data:')) {
      // This is an uploaded file - open it in a new tab
      try {
        // Convert base64 to blob
        const byteString = atob(doc.content.split(',')[1]);
        const mimeString = doc.content.split(',')[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab], { type: mimeString });
        const url = URL.createObjectURL(blob);
        
        // Open in new tab
        const newWindow = window.open(url, '_blank');
        if (!newWindow) {
          alert('Pop-up blocked! Please allow pop-ups to view files.');
        }
        
        // Clean up after a delay
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch (err) {
        console.error('Error opening file:', err);
        // Fallback: download the file
        const link = document.createElement('a');
        link.href = doc.content;
        link.download = doc.title || 'document';
        link.click();
      }
    } else {
      // Regular document - navigate to editor
      navigate(`/editor?id=${id}`);
    }
  };

  const handleNewDocument = () => {
    navigate("/editor");
  };

  const handleDeleteDoc = (id: string | number, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteConfirm(id);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    await docs.remove(deleteConfirm);
    setDeleteConfirm(null);
  };

  const cancelDelete = () => setDeleteConfirm(null);

  const DocIcon = () => (
    <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-blue-500/15">
      <FileText className="w-4 h-4 text-blue-300" />
    </div>
  );

  const TxIcon = () => (
    <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-emerald-500/15">
      <MessageSquare className="w-4 h-4 text-emerald-300" />
    </div>
  );

  const mapTranscriptToResult = (t: TranscriptItem) => ({
    id: String(t.id),
    entityId: t.id,
    audioFileName: t.audioFileName,
    questionsFileName: t.questionsFileName,
    transcript: t.transcript,
    questions: Array.isArray(t.questions) ? t.questions : [],
    answers: Array.isArray(t.answers) ? t.answers : [],
    createdAt: t.created_at || new Date().toISOString(),
    status: 'completed' as const,
    mode: t.mode || 'audio',
  });

  return (
    <Layout>
      <div className="min-h-[calc(100vh-88px)] px-4 sm:px-6 py-4 sm:py-6 text-white">
        {/* DEBUG: Show loading state */}
        {docs.loading && (
          <div className="text-center py-20">
            <p className="text-xl">Loading documents...</p>
          </div>
        )}
        
        {/* DEBUG: Show error */}
        {docs.error && (
          <div className="text-center py-20">
            <p className="text-xl text-red-400">Error: {docs.error}</p>
          </div>
        )}
        
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold mb-1">Documents Hub</h1>
            <p className="text-slate-300 text-xs sm:text-sm">
              {docs.items.length} documents • {transcripts.items.length} transcriptions
            </p>
          </div>

          <button
            onClick={handleNewDocument}
            className="inline-flex items-center justify-center gap-2 px-5 h-11 sm:h-10 rounded-lg text-sm font-semibold
                       text-white border-0 w-full sm:w-auto
                       bg-gradient-to-r from-blue-500 to-fuchsia-500 hover:from-blue-400 hover:to-fuchsia-400
                       shadow-[0_0_18px_rgba(59,130,246,0.45)] active:scale-95 transition-transform"
          >
            <Plus className="w-4 h-4" />
            New Document
          </button>
        </div>

        <div className="glass-panel rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
          <div className="flex-1">
            <input
              placeholder="Search documents..."
              className="w-full h-10 sm:h-9 rounded bg-white/5 border border-white/15 px-3 text-sm
                         placeholder:text-slate-400 focus:outline-none focus:border-white/30 transition-colors"
            />
          </div>
          <div className="flex gap-2 justify-center md:justify-end text-xs text-slate-300">Auto-sorted by last updated</div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Documents */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base sm:text-lg font-semibold">Regular Documents</h2>
              <span className="text-xs text-slate-400">{docs.items.length}</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4">
              {docs.items.map((doc) => (
                <div
                  key={doc.id}
                  className="glass-panel rounded-xl p-4 text-left w-full sm:w-[320px] lg:w-[360px] hover:bg-white/5 active:bg-white/10 transition-colors relative group"
                >
                  <button onClick={() => handleOpenDoc(doc.id)} className="w-full text-left">
                    <div className="flex items-start gap-3">
                      <DocIcon />
                      <div className="flex-1 min-w-0">
                        <h2 className="font-semibold truncate text-base">{doc.title || 'Untitled'}</h2>
                        <div className="mt-1 flex items-center gap-2 text-[11px]">
                          <span className="px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300">
                            {doc.paperFormat || 'APA'}
                          </span>
                          <span className="text-slate-400">{doc.wordCount ?? 0} words</span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between text-[11px] text-slate-400">
                      <span>Updated {doc.updated_at ? new Date(doc.updated_at).toLocaleDateString() : '—'}</span>
                    </div>
                  </button>
                  <button
                    onClick={(e) => handleDeleteDoc(doc.id, e)}
                    className="absolute top-3 right-3 w-9 h-9 sm:w-8 sm:h-8 rounded-lg bg-red-500/10 hover:bg-red-500/20 active:bg-red-500/30 border border-red-500/30 hover:border-red-500/50 flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-200 z-10"
                    title="Delete document"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              ))}
              {docs.items.length === 0 && (
                <div className="text-slate-400 text-sm">No documents yet.</div>
              )}
            </div>
          </div>

          {/* Transcriptions */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base sm:text-lg font-semibold">Transcriptions</h2>
              <span className="text-xs text-slate-400">{transcripts.items.length}</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4">
              {transcripts.items.map((tx) => (
                <div
                  key={tx.id}
                  className="glass-panel rounded-xl p-4 text-left w-full sm:w-[320px] lg:w-[360px] hover:bg-white/5 active:bg-white/10 transition-colors relative group cursor-pointer"
                  onClick={() => setSelectedTranscript(tx)}
                >
                  <div className="flex items-start gap-3">
                    <TxIcon />
                    <div className="flex-1 min-w-0">
                      <h2 className="font-semibold truncate text-base">{tx.title || tx.audioFileName || 'Transcript'}</h2>
                      <div className="mt-1 flex items-center gap-2 text-[11px]">
                        <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300">
                          {tx.mode || 'audio'}
                        </span>
                        <span className="text-slate-400">{tx.questions?.length || 0} Qs</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between text-[11px] text-slate-400">
                    <span>{tx.audioFileName}</span>
                    <span>{tx.updated_at ? new Date(tx.updated_at).toLocaleDateString() : '—'}</span>
                  </div>
                </div>
              ))}
              {transcripts.items.length === 0 && (
                <div className="text-slate-400 text-sm">No transcriptions yet.</div>
              )}
            </div>
          </div>
        </div>

        {deleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="glass-panel rounded-2xl p-5 sm:p-6 max-w-md w-full border-2 border-red-500/30">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Delete Document</h3>
                  <p className="text-sm text-slate-400">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-slate-300 mb-6">
                Are you sure you want to delete "<strong>{docs.items.find(d => d.id === deleteConfirm)?.title}</strong>"?
                All content will be permanently removed.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={cancelDelete}
                  className="flex-1 px-4 py-3 sm:py-2 rounded-lg border border-white/20 hover:bg-white/5 active:bg-white/10 text-white font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-3 sm:py-2 rounded-lg bg-red-500 hover:bg-red-600 active:bg-red-700 text-white font-semibold transition-all shadow-lg shadow-red-500/30"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {selectedTranscript && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
            <div className="w-full max-w-7xl max-h-[90vh] bg-slate-950 rounded-2xl border border-white/10 overflow-hidden shadow-2xl">
              <WhiptTranscriptsPanel
                initialResult={mapTranscriptToResult(selectedTranscript)}
                onClose={() => setSelectedTranscript(null)}
                onSaved={() => transcripts.reload()}
                embedded={true}
              />
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DocumentsPage;